import { CopyOutlined } from '@ant-design/icons';
import { message, Select, Tabs, type TabsProps, Tooltip } from 'antd';
import axios from 'axios';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import { TOKEN } from '../../common/constants';
import {
  formatCurrency,
  formatNumber,
  truncateToChars,
} from '../../common/utils';
import ActionBtn from '../../components/ActionBtn';
import InfiniteSelect from '../../components/infinite-select';
import PageBreadcrumb from '../../components/PageBreadcrumb';
import useRouter from '../../hooks/useRouter';
import type { AppContextType } from '../../types/appContext.type';
import { GET_SECTIONS } from './graphql/queries';
import ReligiousTransparencyAnalysis from './section/components/ReligiousTransparencyAnalysis';
import SexuallyInappropriateAnalyses from './section/components/SexuallyInappropriateAnalyses';

export type SelectValue = {
  label: string;
  value: string;
};

const Section = () => {
  const {
    state: { bookName },
  } = useContext(AppContext) as AppContextType;
  const {
    params: { bookId },
    navigate,
  } = useRouter();

  const [searchParams, setSearchParams] = useSearchParams();
  const [copyLoading, setCopyLoading] = useState(false);

  useEffect(() => {
    // If no tab param exists, set default to factCheck
    if (
      !searchParams.get('tab') ||
      !['sexually-inappropriate'].includes(searchParams.get('tab')!)
    ) {
      searchParams.set('tab', 'sexually-inappropriate');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, []);

  const activeTabFromURL = (searchParams.get('tab') ||
    'sexually-inappropriate') as 'sexually-inappropriate';

  const [externalFiltersSection, setExternalFilterSection] = useState<{
    section?: {
      label: string;
      value: string;
      key: string;
    } | null;
    isProcessed?: boolean;
  }>();

  const [isDataPresent, setIsDataPresent] = useState(false);

  const [total, setTotal] = useState<{ token: number; count: number }>({
    token: 0,
    count: 0,
  });

  const [activeTab, setActiveTab] =
    useState<'sexually-inappropriate'>(activeTabFromURL);

  useEffect(() => {
    const newTab = (searchParams.get('tab') ||
      'sexually-inappropriate') as 'sexually-inappropriate';
    setActiveTab(newTab);
  }, [searchParams]);

  useEffect(() => {
    setTotal({ token: 0, count: 0 });
  }, [activeTab]);

  const handleTabChange = (key: string) => {
    const newTab = key as 'sexually-inappropriate';
    setActiveTab(newTab);
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set('tab', newTab);
      return updated;
    });
  };

  const items: TabsProps['items'] = [
    // commenting for future use
    // {
    //   key: 'factCheck',
    //   label: 'FactCheck Analysis',
    //   children: (
    //     <FactCheckAnalyses
    //       externalFiltersSection={externalFiltersSection}
    //       onTotalChange={setTotal}
    //       activeTab={activeTab}
    //       key={`${activeTab}${JSON.stringify(externalFiltersSection)}`}
    //     />
    //   ),
    // },
    // {
    //   key: 'category',
    //   label: 'Category Analysis',
    //   children: (
    //     <CategoryAnalyses
    //       externalFiltersSection={externalFiltersSection}
    //       onTotalChange={setTotal}
    //       activeTab={activeTab}
    //       key={`${activeTab}${JSON.stringify(externalFiltersSection)}`}
    //     />
    //   ),
    // },
    {
      key: 'sexually-inappropriate',
      label: 'Sexually Inappropriate',
      children: (
        <SexuallyInappropriateAnalyses
          externalFiltersSection={externalFiltersSection}
          onTotalChange={setTotal}
          activeTab={activeTab}
          key={`${activeTab}${JSON.stringify(externalFiltersSection)}`}
          setIsDataPresent={setIsDataPresent}
        />
      ),
    },
    {
      key: 'religious-transparency',
      label: 'Religious Transparency',
      children: (
        <ReligiousTransparencyAnalysis
          externalFiltersSection={externalFiltersSection}
          onTotalChange={setTotal}
          activeTab={activeTab}
          key={`${activeTab}${JSON.stringify(externalFiltersSection)}`}
          setIsDataPresent={setIsDataPresent}
        />
      ),
    },
  ];

  const onSelect = (name: string, value: SelectValue) => {
    setExternalFilterSection((prev) => ({
      ...prev,
      [name]: value.value === 'all' ? undefined : value,
    }));
  };

  const handleCopyButton = async () => {
    setCopyLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_REST_URL}/api/section/${externalFiltersSection?.section?.key}/copy-section`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(TOKEN)}`,
          },
        },
      );
      const data =
        typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data);
      await navigator.clipboard.writeText(data);
      message.success('Data copied to clipboard');
    } catch (error) {
      message.error('Failed to copy data, Please try again!');
      console.error('Error copying section:', error);
    } finally {
      setCopyLoading(false);
    }
  };

  return (
    <>
      <PageBreadcrumb
        items={[
          { title: <NavLink to="/">Book</NavLink> },
          {
            title: (
              <Tooltip title={bookName ?? ''}>
                {truncateToChars(bookName ?? '', 100)}
              </Tooltip>
            ),
          },
        ]}
      />
      <div className="analyses-tab-wrapper">
        <Tabs
          items={items}
          activeKey={activeTab}
          defaultActiveKey="1"
          onChange={handleTabChange}
          tabBarExtraContent={
            <div className="d-flex gap-24">
              <div className="d-flex gap-24 align-center">
                <div>
                  <b>Total Token: </b>
                  {formatNumber(+(total.token ?? 0))}
                </div>
                <div>
                  <b>Total Price: </b>
                  {formatCurrency({ value: +(total.count ?? 0) })}
                </div>
              </div>
              <div className="d-flex gap-8">
                <InfiniteSelect
                  allowSearch
                  allowClear
                  query={GET_SECTIONS}
                  variableSelector={({ skip, limit, search }) => {
                    return {
                      filters: {
                        ...(search && { title: { containsi: search } }),
                        book: {
                          documentId: {
                            eq: bookId ?? '',
                          },
                        },
                      },
                      pagination: { start: skip, limit },
                    };
                  }}
                  countSelector={(data) =>
                    data.sections_connection?.pageInfo?.total ?? 0
                  }
                  dataSelector={(data) => {
                    return (
                      data.sections_connection?.nodes?.map((section) => ({
                        label: section?.title ?? '',
                        value: section?.title ?? '',
                        key: section?.documentId,
                      })) ?? []
                    );
                  }}
                  placeholder="Choose Section"
                  size="middle"
                  maxTagCount="responsive"
                  className="pointer w-200"
                  labelRender={(props) => (
                    <span className="text-sm">{props.label}</span>
                  )}
                  onSelect={(value) =>
                    onSelect('section', value as SelectValue)
                  }
                  onClear={() =>
                    setExternalFilterSection((prev) => ({
                      ...prev,
                      section: null,
                    }))
                  }
                />
                {!isEmpty(externalFiltersSection?.section) && (
                  <Tooltip title="Copy Section Data">
                    <ActionBtn
                      icon={<CopyOutlined />}
                      type="text"
                      disabled={!isDataPresent}
                      loading={copyLoading}
                      onClick={handleCopyButton}
                      size="large"
                    />
                  </Tooltip>
                )}
                <Select
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Processed', value: true },
                    { label: 'Not Processed', value: false },
                  ]}
                  defaultValue="all"
                  size="middle"
                  onChange={(value) =>
                    onSelect('isProcessed', { label: 'Processed', value })
                  }
                  placeholder="Processed"
                  className="pointer w-200"
                />
              </div>
            </div>
          }
        />
      </div>
    </>
  );
};

export default Section;
